import { useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '@/lib/stores/authStore';
import { refreshAccessToken } from '@/lib/api/auth';
import { createComponentLogger } from '@/lib/utils/logger';

const logger = createComponentLogger('useTokenRefresh');

/**
 * Session Recovery on App Mount
 *
 * CRITICAL: When app mounts, check if token is expired but project-reference exists.
 * Attempt silent refresh BEFORE showing login screen.
 * Only redirect to login if refresh genuinely fails (401/403).
 *
 * This prevents users from seeing login screen when they reopen browser
 * within the 14-day refresh token window.
 */
export async function recoverSession(): Promise<boolean> {
  const { token, user } = useAuthStore.getState();
  const projectReference = localStorage.getItem('project-reference');

  // If user is already authenticated with valid token, no recovery needed
  if (token && user && !isTokenExpired(token)) {
    logger.log('Session recovery: Token is still valid, no recovery needed');
    return true;
  }

  // If token is expired but project-reference exists, attempt silent refresh
  if (projectReference) {
    logger.log(
      'Session recovery: Token expired but project-reference exists, attempting silent refresh'
    );

    // Attempt refresh with clearAuthOnFailure: false to avoid logging out on transient errors
    const newToken = await refreshAccessToken({ clearAuthOnFailure: false });

    if (newToken) {
      logger.log('Session recovery: Silent refresh successful');
      return true;
    } else {
      logger.warn(
        'Session recovery: Silent refresh failed - refresh token may be invalid or expired'
      );
      // Only clear auth if refresh genuinely failed (not transient error)
      // The refreshAccessToken function already handles this with clearAuthOnFailure: false
      return false;
    }
  }

  // No project-reference means we can't refresh - user must login
  logger.warn('Session recovery: No project-reference found, cannot recover session');
  return false;
}

/**
 * Token expiry buffer time in milliseconds
 * Refresh the token 5 minutes before it actually expires
 */
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Minimum interval between refresh checks
 * Reduced from 60s to 30s for better coverage and faster recovery
 */
const REFRESH_CHECK_INTERVAL_MS = 30 * 1000; // Check every 30 seconds

/**
 * Delay before retrying a failed proactive refresh
 */
const PROACTIVE_REFRESH_RETRY_DELAY_MS = 5 * 1000; // 5 seconds

/**
 * Decode JWT token and extract expiration time
 */
function getTokenExpiry(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    // exp is in seconds, convert to milliseconds
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    logger.error('Failed to decode token');
    return null;
  }
}

/**
 * Check if token should be refreshed
 * Returns true if token will expire within the buffer time
 */
function shouldRefreshToken(token: string | null): boolean {
  if (!token) {
    return false;
  }

  const expiry = getTokenExpiry(token);
  if (!expiry) {
    return false;
  }

  const now = Date.now();
  const timeUntilExpiry = expiry - now;

  // Refresh if less than 5 minutes until expiry
  return timeUntilExpiry < TOKEN_REFRESH_BUFFER_MS && timeUntilExpiry > 0;
}

/**
 * Check if token is already expired
 */
function isTokenExpired(token: string | null): boolean {
  if (!token) {
    return true;
  }

  const expiry = getTokenExpiry(token);
  if (!expiry) {
    return false; // Can't determine, assume valid
  }

  return Date.now() >= expiry;
}

/**
 * Custom hook for proactive token refresh
 *
 * This hook monitors the access token expiration and automatically
 * refreshes it before it expires, preventing users from being logged out.
 *
 * Features:
 * - Proactive refresh 5 minutes before token expires
 * - Automatic retry on failure
 * - Handles visibility change (refreshes when tab becomes visible after being hidden)
 * - Memory-safe with proper cleanup
 *
 * @example
 * ```tsx
 * function App() {
 *   // Enable token refresh monitoring
 *   useTokenRefresh();
 *
 *   return <YourApp />;
 * }
 * ```
 */
export function useTokenRefresh(): void {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated());
  const isInitialized = useAuthStore((state) => state.isInitialized);

  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRefreshingRef = useRef(false);

  /**
   * Perform token refresh with immediate retry on failure
   * CRITICAL: If proactive refresh fails, we retry immediately instead of waiting for next interval
   */
  const performRefresh = useCallback(async (): Promise<boolean> => {
    if (isRefreshingRef.current) {
      logger.log('Token refresh already in progress, skipping');
      return false;
    }

    isRefreshingRef.current = true;

    try {
      // Log current token expiry for debugging
      const currentToken = useAuthStore.getState().token;
      if (currentToken) {
        const expiry = getTokenExpiry(currentToken);
        if (expiry) {
          const timeUntilExpiry = Math.round((expiry - Date.now()) / 1000);
          logger.log(`Proactively refreshing token (${timeUntilExpiry}s until expiry)...`);
        } else {
          logger.log('Proactively refreshing token...');
        }
      }

      const newToken = await refreshAccessToken({ clearAuthOnFailure: false });

      if (newToken) {
        logger.log('Token refreshed successfully');
        return true;
      } else {
        logger.warn('Token refresh returned null; keeping current session active');

        // CRITICAL: Retry immediately after a short delay if proactive refresh fails
        // This gives us a second chance before the next 30s interval
        logger.log(`Scheduling immediate retry in ${PROACTIVE_REFRESH_RETRY_DELAY_MS}ms`);
        retryTimerRef.current = setTimeout(async () => {
          if (isRefreshingRef.current) {
            logger.log('Retry skipped - another refresh is in progress');
            return;
          }

          logger.log('Attempting immediate retry of token refresh...');
          const retryToken = await refreshAccessToken({ clearAuthOnFailure: false });
          if (retryToken) {
            logger.log('Token retry successful');
          } else {
            logger.warn('Token retry also failed - will retry on next interval');
          }
        }, PROACTIVE_REFRESH_RETRY_DELAY_MS);

        return false;
      }
    } catch (error) {
      logger.error('Token refresh failed:', error);
      return false;
    } finally {
      isRefreshingRef.current = false;
    }
  }, []);

  /**
   * Check token and refresh if needed
   * Enhanced with detailed logging for debugging
   */
  const checkAndRefresh = useCallback(async () => {
    const currentToken = useAuthStore.getState().token;

    if (!currentToken) {
      logger.log('No token to refresh');
      return;
    }

    // Log detailed token state for debugging
    const expiry = getTokenExpiry(currentToken);
    if (expiry) {
      const timeUntilExpiry = Math.round((expiry - Date.now()) / 1000);
      const minutesUntilExpiry = Math.round(timeUntilExpiry / 60);
      logger.log(`Token check: ${minutesUntilExpiry}min until expiry`, {
        timeUntilExpiry,
        expiryTimestamp: expiry,
        currentTime: Date.now(),
      });
    }

    // Check if token is already expired
    if (isTokenExpired(currentToken)) {
      logger.warn('Token is already expired, attempting refresh');
      await performRefresh();
      return;
    }

    // Check if token should be refreshed (approaching expiry)
    if (shouldRefreshToken(currentToken)) {
      logger.log('Token approaching expiry (within 5min buffer), refreshing proactively');
      await performRefresh();
    } else {
      logger.log('Token still valid, no refresh needed');
    }
  }, [performRefresh]);

  /**
   * Handle visibility change - refresh when tab becomes visible
   */
  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'visible') {
      logger.log('Tab became visible, checking token');
      checkAndRefresh();
    }
  }, [checkAndRefresh]);

  /**
   * Handle window focus - refresh when window gains focus
   */
  const handleFocus = useCallback(() => {
    logger.log('Window focused, checking token');
    checkAndRefresh();
  }, [checkAndRefresh]);

  // Set up periodic token check
  useEffect(() => {
    if (!isInitialized) {
      // Clear any existing timer if not initialized
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      return;
    }

    // CRITICAL: Attempt session recovery on app mount
    // This handles the case where user closed browser and reopened it
    // Access token is expired, but refresh token is still valid (within 14 days)
    if (!isAuthenticated) {
      logger.log('App mounted but not authenticated - attempting session recovery');
      recoverSession().then((recovered) => {
        if (recovered) {
          logger.log('Session recovery successful - user is now authenticated');
          // No need to start the interval here; the change in isAuthenticated
          // will trigger this useEffect to re-run and set up the interval safely.
        } else {
          logger.warn('Session recovery failed - user must login');
          // User will be redirected to login by ProtectedRoute
        }
      });
      return;
    }

    logger.log('Setting up proactive token refresh');

    // Initial check
    checkAndRefresh();

    // Set up periodic check
    refreshTimerRef.current = setInterval(() => {
      checkAndRefresh();
    }, REFRESH_CHECK_INTERVAL_MS);

    // Clean up on unmount or when auth state changes
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [isInitialized, isAuthenticated, checkAndRefresh]);

  // Set up visibility and focus listeners
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isAuthenticated, handleVisibilityChange, handleFocus]);
}

/**
 * Get token expiry information for debugging
 */
export function getTokenExpiryInfo(): {
  expiresAt: Date | null;
  expiresIn: number | null;
  isExpired: boolean;
  shouldRefresh: boolean;
} {
  const token = useAuthStore.getState().token;

  if (!token) {
    return {
      expiresAt: null,
      expiresIn: null,
      isExpired: true,
      shouldRefresh: false,
    };
  }

  const expiry = getTokenExpiry(token);

  if (!expiry) {
    return {
      expiresAt: null,
      expiresIn: null,
      isExpired: false,
      shouldRefresh: false,
    };
  }

  const now = Date.now();
  const expiresIn = expiry - now;

  return {
    expiresAt: new Date(expiry),
    expiresIn: Math.max(0, expiresIn),
    isExpired: expiresIn <= 0,
    shouldRefresh: expiresIn < TOKEN_REFRESH_BUFFER_MS && expiresIn > 0,
  };
}
